/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Tab, Tabs } from '@material-ui/core'
import { Link, useHistory, useParams } from 'react-router-dom'

import { AdminAnywhereRequired } from 'components/partials'
import { ActionButton, CenteredLoader, PageWrapper, StyledHeader, ViewSpacerWrapper, ViewWrapper } from 'components/widgets'
import { RenderCounter } from 'components/widgets/RenderCounter'

import { Orgs } from 'models'
import { ALL_ORGS_SELECTED, PATH_ADMIN } from 'lib/constants'
import { trans, TranslationGroup, TranslationKey } from 'lib/types'
import { isSuperAdmin } from 'lib/utils/auth'

import { useGlobal } from 'contexts/GlobalProvider'

import { GroupsTab } from './Partials/GroupsTab'
import CreateOrgModal from './Partials/CreateOrgModal'
import { EditOrgView } from './EditOrgView'
import { ManageFirmwareVersions } from '../GeneralSettingsPage/ManageFirmwareVersions'

const tabCss = { fontFamily: 'Montserrat' }

interface AdminParams {
  section: string
}

export const AdminView = () => {
  const common: TranslationGroup = trans.common()
  const adminTranslation: TranslationGroup = trans.merge(TranslationKey.ADMIN)
  const [selectedTabIndex, setSelectedTabIndex] = useState(0)
  const [createOrgModalOpen, setCreateOrgModalOpen] = useState(false)

  const { loading, selectedOrgId, setSelectedOrg, setSelectedOrgId } = useGlobal()
  const orgsById = Orgs.use(({ orgsById }) => orgsById)
  const history = useHistory()

  const selectedOrg = useMemo(() => {
    return selectedOrgId ? orgsById[selectedOrgId] : undefined
  }, [selectedOrgId, orgsById])

  const { section } = useParams<AdminParams>()

  useEffect(() => {
    switch (section) {
      case 'groups':
        return setSelectedTabIndex(1)
      case 'firmware':
        return setSelectedTabIndex(2)
      default:
        return setSelectedTabIndex(0)
    }
  }, [section])

  // Auto-switch the active tab ONLY when the org itself changes (user picks a new org
  // from the nav or from the GroupsTab edit button). We don't react to plain tab clicks
  // here — those are handled below so the user's explicit tab click is always respected.
  const lastOrgIdRef = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    const currentOrgId = selectedOrg?.id ?? null
    if (lastOrgIdRef.current === currentOrgId) return
    lastOrgIdRef.current = currentOrgId
    if (!selectedOrg) return
    if (section === 'firmware') return
    if (Orgs.isGroup(selectedOrg) && section !== 'groups') {
      history.replace(PATH_ADMIN.GROUPS)
    } else if (Orgs.isProperty(selectedOrg) && section === 'groups') {
      history.replace(PATH_ADMIN.PROPERTIES)
    }
  }, [selectedOrg?.id, selectedOrg?.orgType, section, history])

  // When the user clicks a tab whose type doesn't match the currently-selected org,
  // clear the selection so the tab shows its default content (GroupsTab list or
  // "select a property" placeholder) instead of getting stuck on the other tab.
  const lastSectionRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    const prev = lastSectionRef.current
    lastSectionRef.current = section
    if (prev === undefined || prev === section) return // skip initial mount
    if (section === 'firmware') return
    if (!selectedOrg) return
    const propertyTabWithGroup = section !== 'groups' && Orgs.isGroup(selectedOrg)
    const groupsTabWithProperty = section === 'groups' && Orgs.isProperty(selectedOrg)
    if (propertyTabWithGroup || groupsTabWithProperty) {
      setSelectedOrg(null)
      setSelectedOrgId(ALL_ORGS_SELECTED)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section])

  const renderTabContent = () => {
    switch (selectedTabIndex) {
      case 0:
        // Property tab shows the EditOrgView for the globally selected property.
        return selectedOrg && Orgs.isProperty(selectedOrg) ? (
          <EditOrgView org={selectedOrg} />
        ) : (
          <p css={{ padding: '24px 0', opacity: 0.7 }}>Select a property from the navigation to view and edit it.</p>
        )
      case 1:
        // Groups tab: when a group is selected, edit it inline; otherwise show the (lazy-loaded) groups table.
        return selectedOrg && Orgs.isGroup(selectedOrg) ? <EditOrgView org={selectedOrg} /> : <GroupsTab />
      case 2:
        return <ManageFirmwareVersions />
      default:
        return null
    }
  }

  return (
    <AdminAnywhereRequired>
      <PageWrapper>
        <ViewWrapper>
          <ViewSpacerWrapper>
            {loading ? (
              <CenteredLoader />
            ) : (
              <>
                <RenderCounter name={'AdminView'} />

                <StyledHeader>Admin</StyledHeader>
                <div css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Tabs value={selectedTabIndex} onChange={(e, newValue) => setSelectedTabIndex(newValue)}>
                    <Tab label={common.property} css={tabCss} component={Link} to={PATH_ADMIN.PROPERTIES} />
                    <Tab label={common.groups} css={tabCss} component={Link} to={PATH_ADMIN.GROUPS} />
                    {isSuperAdmin() && (
                      <Tab label={common.firmware} css={tabCss} component={Link} to={PATH_ADMIN.FIRMWARE} />
                    )}
                  </Tabs>
                  {isSuperAdmin() && selectedTabIndex < 2 && (
                    <ActionButton onClick={() => setCreateOrgModalOpen(true)}>
                      {adminTranslation.add}{' '}
                      {selectedTabIndex === 1 ? adminTranslation.group : adminTranslation.property}
                    </ActionButton>
                  )}
                </div>

                {renderTabContent()}

                <CreateOrgModal
                  open={createOrgModalOpen}
                  setModalOpen={setCreateOrgModalOpen}
                  closeModal={() => setCreateOrgModalOpen(false)}
                  groupsMode={selectedTabIndex === 1}
                />
              </>
            )}
          </ViewSpacerWrapper>
        </ViewWrapper>
      </PageWrapper>
    </AdminAnywhereRequired>
  )
}

export default React.memo(AdminView)
